import { Form } from '@mairistem/composants';
import { Sens } from '@mairistem/comptabilite';
import { Combobox, Date } from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import React from 'react';
import AUTO from '../../../../constants/common/common';
import { TYPE_BORDEREAU } from '../../../../constants/mouvements/typeBordereau';
import type { BordereauWithState } from '../../../../interfaces/pieces/TreeRow';
import CustomToggle from './CustomToggle';

interface BordereauFormProps {
	bordereau: BordereauWithState;
	customBordereau?: Partial<BordereauWithState>;
	setCustomBordereau: (
		value: React.SetStateAction<Partial<BordereauWithState>>,
	) => void;
	series?: DropdownOption[];
	libelleDisabled?: boolean;
	numeroDisabled?: boolean;
	dateDisabled?: boolean;
	sensDisabled?: boolean;
	serieDisabled?: boolean;
	typeDisabled?: boolean;
}

const OPTIONS_TYPE = [
	{
		key: 1,
		text: 'Ordinaire',
		value: TYPE_BORDEREAU.DEPENSE.ORDINAIRE,
	},
	{
		key: 2,
		text: 'Annulation-Réduction',
		value: TYPE_BORDEREAU.DEPENSE.ANNUL_REDUC,
	},
	{
		key: 3,
		text: 'Rejet',
		value: TYPE_BORDEREAU.REJET,
	},
] as const;

const CHAMPS = {
	TYPE: 'typePes',
	NUMERO: 'numero',
	DATE_EMISSION: 'dateEmission',
	IDENTIFIANT_SERIE: 'identifiantSerie',
	LIBELLE: 'libelle',
} as const;

const BordereauForm = ({
	bordereau,
	customBordereau,
	setCustomBordereau,
	series,
	libelleDisabled,
	numeroDisabled,
	dateDisabled,
	sensDisabled,
	serieDisabled,
	typeDisabled,
}: BordereauFormProps) => {
	const handleBordereauChange = (name: string, value: any) => {
		setCustomBordereau((old) => ({
			...old,
			[name]: value,
		}));
	};

	const handleChange = (_e, { name, value }) =>
		handleBordereauChange(name, value);

	const handleChangeDate = (value: string, name: string) =>
		handleBordereauChange(name, value);

	const handleToggleChange = (data) =>
		handleBordereauChange('sens', data ? Sens.RECETTE : Sens.DEPENSE);

	React.useEffect(() => {
		if (isNil(customBordereau)) {
			setCustomBordereau({
				sens: bordereau?.sens ?? Sens.DEPENSE,
				typePes: bordereau?.typePes ?? TYPE_BORDEREAU.DEPENSE.ORDINAIRE,
				numero: bordereau?.numero ?? AUTO,
				dateEmission:
					bordereau?.dateEmission ?? dayjs().format('YYYY-MM-DD HH:mm:ss'),
				identifiantSerie: bordereau?.identifiantSerie ?? undefined,
				libelle: bordereau?.libelle ?? '',
			});
		}
	}, [bordereau, customBordereau, setCustomBordereau]);

	return (
		<Form>
			<Form.Group>
				<Form.Field
					control={CustomToggle}
					data-testid="sensBordereau"
					disabled={sensDisabled || bordereau?.piecesCount > 0}
					label="Sens"
					name="sens"
					onChange={handleToggleChange}
					value={customBordereau?.sens}
					width={series.length > 1 ? 3 : 4}
				/>
				<Form.Field
					control={Combobox}
					data-testid="typeBordereau"
					disabled={typeDisabled}
					label="Type"
					name={CHAMPS.TYPE}
					onChange={handleChange}
					options={OPTIONS_TYPE}
					required
					selection
					value={customBordereau?.typePes}
					width={series.length > 1 ? 4 : 5}
				/>
				<Form.Input
					data-testid="numeroBordereau"
					disabled={numeroDisabled}
					label="Numéro"
					name={CHAMPS.NUMERO}
					onChange={handleChange}
					readOnly
					value={customBordereau?.numero}
					width={3}
				/>
				<Form.Field
					control={Date}
					data-testid="dateEmissionBordereau"
					disabled={dateDisabled}
					fluid
					label="Emission"
					name={CHAMPS.DATE_EMISSION}
					onChange={handleChangeDate}
					required
					value={customBordereau?.dateEmission}
					width={series.length > 1 ? 3 : 4}
				/>
				{series.length > 1 && (
					<Form.Field
						control={Combobox}
						data-testid="identifiantSerie"
						disabled={serieDisabled}
						label="Série"
						name={CHAMPS.IDENTIFIANT_SERIE}
						onChange={handleChange}
						options={series}
						selection
						value={customBordereau?.identifiantSerie}
						width={4}
					/>
				)}
			</Form.Group>
			<Form.Input
				data-testid="libelleBordereau"
				disabled={libelleDisabled}
				label="Libellé"
				name={CHAMPS.LIBELLE}
				onChange={handleChange}
				required
				value={customBordereau?.libelle}
			/>
		</Form>
	);
};

export default BordereauForm;
